import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "next-themes";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { ChevronDown, User, LogOut, Menu, Megaphone, Sparkles, Settings, LogIn, Grip, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { AccountSidebar } from "./AccountSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useCachedAvatar } from "@/hooks/useCachedAvatar";
import { NotificationDropdown } from "./NotificationDropdown";
import { AppLauncher } from "./AppLauncher";
import { UserSettingsDialog } from "./UserSettingsDialog";
import queueOSLogoDark from "@/assets/queueos-logo-dark.svg";
import queueOSLogoLight from "@/assets/queueos-logo-light.svg";

export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, signOut } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { avatarUrl } = useCachedAvatar();
  const isMobile = useIsMobile();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const links = [
    { label: "Features", href: "#features", id: "features" },
    { label: "Pricing", href: "#pricing", id: "pricing" },
    { label: "About", href: "#about", id: "about" },
  ];

  // Different links for De Anza page
  const deAnzaLinks = [
    { label: "Back to Homepage", href: "https://queueos.com", id: "homepage", isExternal: true },
  ];

  type LinkType = typeof links[0] & { isExternal?: boolean };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hovering, setHovering] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [indicator, setIndicator] = useState({ x: 0, width: 0, height: 0, ready: false });

  const moveToIndex = (idx: number) => {
    const container = containerRef.current;
    const el = linkRefs.current[idx];
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const x = r.left - cRect.left;
    setIndicator({ x, width: r.width, height: r.height, ready: true });
  };

  useEffect(() => {
    // initial position + resize handling
    moveToIndex(activeIndex);
    const onResize = () => {
      const idx = hovering && hoveredIndex != null ? hoveredIndex : activeIndex;
      moveToIndex(idx);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!hovering) moveToIndex(activeIndex);
  }, [activeIndex, hovering]);

  useEffect(() => {
    // Observe sections to sync active link while scrolling
    const ids = links.map((l) => l.id);
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (sections.length === 0) return;

    const updateActiveSection = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Check if we're at the very bottom of the page
      if (scrollY + windowHeight >= documentHeight - 50) {
        setActiveIndex(ids.length - 1); // Set to last section (About)
        return;
      }
      
      // If we're at the very top of the page (before features section), show no highlight
      if (scrollY < 100) {
        setActiveIndex(-1);
        return;
      }
      
      // Find the section that's currently most visible in the viewport center
      let currentSectionIndex = -1;
      const viewportCenter = scrollY + windowHeight / 2;
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const rect = section.getBoundingClientRect();
        const sectionTop = scrollY + rect.top;
        const sectionBottom = sectionTop + rect.height;
        
        // If viewport center is within this section, or past its start
        if (viewportCenter >= sectionTop && viewportCenter <= sectionBottom) {
          currentSectionIndex = i;
          break;
        } else if (viewportCenter >= sectionTop) {
          currentSectionIndex = i;
        }
      }
      
      setActiveIndex(currentSectionIndex);
    };

    // Initial update
    updateActiveSection();

    // Listen to scroll events
    const handleScroll = () => updateActiveSection();
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <nav className="fixed top-8 left-0 right-0 z-50 pointer-events-none">
      <div className="container mx-auto px-6 relative">
        <div className={`${isMobile ? 'flex justify-between items-center' : 'relative flex items-center justify-between'} bg-background/60 backdrop-blur-md rounded-2xl border border-border/50 shadow-sm pointer-events-auto`}>
          {/* Left: QueueOS logo + name */}
          <div className="pointer-events-auto flex items-center space-x-2 ml-4">
            <button 
              onClick={() => navigate("/")}
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src={resolvedTheme === "dark" ? queueOSLogoLight : queueOSLogoDark} 
                alt="queueOS logo" 
                className="block h-20 w-auto" 
              />
            </button>
          </div>

          {/* Center: Navigation - Desktop only pill - Removed per user request */}

          <div className={`${isMobile ? 'mr-4' : 'mr-4'} pointer-events-auto flex items-center gap-4`}>
            {isMobile ? (
              location.pathname === '/' ? (
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="flex items-center gap-2 bg-background/80 backdrop-blur-sm">
                      <Menu className="h-4 w-4" />
                      Menu
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-full p-0 border-0">
                    <div className="flex flex-col h-full bg-background">
                      {/* User Profile Section at Top */}
                      {user && (
                        <div className="p-6 border-b border-border">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full overflow-hidden bg-primary flex items-center justify-center text-primary-foreground font-medium text-lg">
                              {user?.user_metadata?.avatar_url ? (
                                <img 
                                  src={user.user_metadata.avatar_url} 
                                  alt="Profile"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Fallback to initials if image fails to load
                                    e.currentTarget.style.display = 'none';
                                    if (e.currentTarget.nextSibling) {
                                      (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <span 
                                className={`w-full h-full flex items-center justify-center ${user?.user_metadata?.avatar_url ? 'hidden' : 'flex'}`}
                              >
                                {(user?.user_metadata?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || "U").charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-lg">
                                {user?.user_metadata?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || "User"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {user?.email}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Navigation Links */}
                      <div className="flex-1 p-6">
                        <div className="space-y-4">
                          {/* Current Student button for homepage */}
                          <Button
                            variant="outline"
                            onClick={() => window.open('https://queueos.com/student', '_blank')}
                            className="w-full justify-between py-3 h-auto text-lg font-medium"
                          >
                            Current Student?
                            <ChevronRight className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Bottom Section */}
                      <div className="p-6 border-t border-border space-y-4">
                        {/* Settings Button */}
                        <Button
                          variant="outline"
                          onClick={() => setSettingsOpen(true)}
                          className="w-full justify-start gap-3 py-3 h-auto text-lg"
                        >
                          <Settings className="h-5 w-5" />
                          Settings
                        </Button>
                        
                        {user ? (
                          <>
                            <Button
                              variant="ghost"
                              onClick={() => navigate("/dashboard")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <User className="h-5 w-5" />
                              Dashboard
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => navigate("/app-launcher")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <Grip className="h-5 w-5" />
                              App Launcher
                            </Button>
                            <Button
                              variant="ghost"
                              asChild
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <a data-canny-link href="https://help.queueos.com/collection/changelog" target="_blank" rel="noopener noreferrer">
                                <Sparkles className="h-5 w-5" />
                                See what's new
                              </a>
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => signOut()}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg text-destructive hover:text-destructive"
                            >
                              <LogOut className="h-5 w-5" />
                              Sign Out
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="default"
                              onClick={() => navigate("/auth?mode=signin")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <User className="h-5 w-5" />
                              Sign In
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => navigate("/auth?mode=signup")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <LogIn className="h-5 w-5" />
                              Sign Up
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              ) : (
                <Drawer>
                  <DrawerTrigger asChild>
                    <Button variant="outline" size="sm" className="flex items-center gap-2 bg-background/80 backdrop-blur-sm">
                      <Menu className="h-4 w-4" />
                      Menu
                    </Button>
                  </DrawerTrigger>
                  <DrawerContent className="max-h-[80vh]">
                    <div className="flex flex-col h-full bg-background overflow-y-auto">
                      {/* User Profile Section at Top */}
                      {user && (
                        <div className="p-6 border-b border-border">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full overflow-hidden bg-primary flex items-center justify-center text-primary-foreground font-medium text-lg">
                              {user?.user_metadata?.avatar_url ? (
                                <img 
                                  src={user.user_metadata.avatar_url} 
                                  alt="Profile"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Fallback to initials if image fails to load
                                    e.currentTarget.style.display = 'none';
                                    if (e.currentTarget.nextSibling) {
                                      (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <span 
                                className={`w-full h-full flex items-center justify-center ${user?.user_metadata?.avatar_url ? 'hidden' : 'flex'}`}
                              >
                                {(user?.user_metadata?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || "U").charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-lg">
                                {user?.user_metadata?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || "User"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {user?.email}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Navigation Links */}
                      <div className="flex-1 p-6">
                        <div className="space-y-4">
                          {/* De Anza page links */}
                          {location.pathname === '/deanza' && deAnzaLinks.map((link: LinkType, i) => (
                            <a
                              key={link.href}
                              href={link.href}
                              onClick={(e) => {
                                if (link.isExternal) {
                                  e.preventDefault();
                                  window.open(link.href, '_blank');
                                }
                              }}
                              className="block py-3 px-4 text-lg font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                            >
                              {link.label}
                            </a>
                          ))}
                        </div>
                      </div>
                      
                      {/* Bottom Section */}
                      <div className="p-6 border-t border-border space-y-4">
                        {/* Settings Button */}
                        <Button
                          variant="outline"
                          onClick={() => setSettingsOpen(true)}
                          className="w-full justify-start gap-3 py-3 h-auto text-lg"
                        >
                          <Settings className="h-5 w-5" />
                          Settings
                        </Button>
                        
                        {user ? (
                          <>
                            <Button
                              variant="ghost"
                              onClick={() => navigate("/dashboard")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <User className="h-5 w-5" />
                              Dashboard
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => navigate("/app-launcher")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <Grip className="h-5 w-5" />
                              App Launcher
                            </Button>
                            <Button
                              variant="ghost"
                              asChild
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <a data-canny-link href="https://help.queueos.com/collection/changelog" target="_blank" rel="noopener noreferrer">
                                <Sparkles className="h-5 w-5" />
                                See what's new
                              </a>
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => signOut()}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg text-destructive hover:text-destructive"
                            >
                              <LogOut className="h-5 w-5" />
                              Sign Out
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="default"
                              onClick={() => navigate("/auth?mode=signin")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <User className="h-5 w-5" />
                              Sign In
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => navigate("/auth?mode=signup")}
                              className="w-full justify-start gap-3 py-3 h-auto text-lg"
                            >
                              <LogIn className="h-5 w-5" />
                              Sign Up
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </DrawerContent>
                </Drawer>
              )
            ) : user ? (
              <div className="flex items-center gap-2">
                <AppLauncher />
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSettingsOpen(true)}
                  className="h-8 w-8 p-0"
                >
                  <Settings className="h-4 w-4" />
                </Button>
                <NotificationDropdown />
                <AccountSidebar trigger={
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={avatarUrl || undefined} alt="Profile" />
                      <AvatarFallback>
                        {(user?.user_metadata?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || "U").charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                } />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/auth?mode=signin")}>
                  Sign In
                </Button>
                <Button size="sm" onClick={() => navigate("/auth?mode=signup")}>
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <UserSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </nav>
  );
};
